import { Component, Type, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatSuffix } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { catchError } from 'rxjs';
import { AuthService } from '../../../../core/auth/auth.service';
import { BrandsService } from '../../../../core/brands/brands.service';
import { FidelityBasePageComponent } from '../../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { FidelityOverlayPageWrapperComponent } from '../../../../shared/components/fidelity-overlay-page-wrapper/fidelity-overlay-page-wrapper.component';
import { CUSTOMER_ERRORS } from '../../../../shared/constants/customer.consts';
import { RegistrationConfirmationService } from '../../../landing/registration-confirmation/registration-confirmation.service';
import { ForgotPasswordPageComponent } from '../forgot-password/forgot-password.component';
import { RegisterPageComponent } from '../register/register-page.component';

@Component({
    selector: 'app-sign-in-page',
    imports: [
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        MatButton,
        TranslocoDirective,
        MatSuffix,
        MatProgressSpinner,
        FidelityOverlayPageWrapperComponent,
        MatCheckbox,
    ],
    templateUrl: './sign-in-page.component.html',
    styleUrl: './sign-in-page.component.scss',
})
export class SignInPageComponent extends FidelityBasePageComponent {
    availablePages: { [p: string]: Type<any> } = {
        register: RegisterPageComponent,
        forgotPassword: ForgotPasswordPageComponent,
    };
    signInForm: FormGroup;
    isLoading = signal(false);

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private registrationConfirmationService: RegistrationConfirmationService,
        protected brandsService: BrandsService
    ) {
        super();

        this.signInForm = this.fb.group({
            email: ['', Validators.required],
            password: ['', Validators.required],
            rememberMe: [false],
        });
    }

    signIn() {
        if (this.isLoading()) return;

        this.isLoading.set(true);
        this.authService
            .signIn(this.signInForm.value)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.isLoading.set(false);
                    return this.handlePageError(error);
                })
            )
            .subscribe((res) => {
                this.isLoading.set(false);
                if (res) {
                    this.notifyResult(this.translocoService.translate('fidelity-app.sign-in.notification-caption'));
                    this.fidelityAppNavService.closePageAndClearHistory();
                }
            });
    }

    resendConfirm() {
        if (this.isLoading()) return;

        this.isLoading.set(true);
        this.registrationConfirmationService
            .resendConfirmationEmail({ email: this.signInForm.value.email })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.isLoading.set(false);
                    return this.handlePageError(error);
                })
            )
            .subscribe((_) => {
                this.isLoading.set(false);
                this.notifyResult(this.translocoService.translate('fidelity-app.sign-in.confirmation-sent'));
                this.fidelityAppNavService.closePageAndClearHistory();
            });
    }

    protected readonly CUSTOMER_ERRORS = CUSTOMER_ERRORS;
}
